/**
 * Badge — the §7.4 status badge (style guide §2.6; mission 6 WP03 T020; FR-005).
 *
 * 22px, 12/500, padding 0 8px, radius-sm, with an optional 6px leading dot in
 * Sky-burst that pulses for a running state. `NOT DELIVERABLE` uses the solid
 * ERROR style (§2.6).
 *
 * There is NO severity variant (post-plan T5, post-tasks R21): severity has
 * exactly two render paths, `SeverityBadge` and `SeverityCountChip`, which read
 * the `--severity-*` aliases. A `severity` prop here fails the typecheck.
 */
import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from './cn'

export const badgeVariants = cva(
  'inline-flex h-[22px] shrink-0 items-center gap-1.5 whitespace-nowrap rounded-sm border px-2 text-caption font-medium',
  {
    variants: {
      status: {
        success:
          'border-[var(--color-success-200)] bg-[var(--color-success-50)] text-[var(--color-success-700)]',
        info: 'border-[var(--color-info-200)] bg-[var(--color-info-50)] text-[var(--color-info-800)]',
        neutral: 'border-navy-300 bg-navy-100 text-navy-700',
        warning: 'border-transparent bg-[var(--color-warning-100)] text-[var(--color-warning-800)]',
        error: 'border-[var(--color-error-200)] bg-[var(--color-error-50)] text-[var(--color-error-700)]',
        'not-deliverable': 'border-transparent bg-[var(--color-error-700)] text-white',
      },
    },
    defaultVariants: { status: 'neutral' },
  },
)

export interface BadgeProps extends React.HTMLAttributes<HTMLSpanElement>, VariantProps<typeof badgeVariants> {
  /** A 6px leading dot in Sky-burst (§2.6 running states). */
  dot?: boolean
  /** The dot pulses (Running, Analyzing); stops under reduced motion. */
  pulse?: boolean
}

export const Badge = React.forwardRef<HTMLSpanElement, BadgeProps>(
  ({ className, status, dot = false, pulse = false, children, ...props }, ref) => (
    <span ref={ref} className={cn(badgeVariants({ status, className }))} {...props}>
      {dot ? (
        <span
          aria-hidden
          data-badge-dot=""
          className={cn('size-1.5 shrink-0 rounded-full bg-brand-sky', pulse && 'animate-pulse motion-reduce:animate-none')}
        />
      ) : null}
      {children}
    </span>
  ),
)
Badge.displayName = 'Badge'
